import axios, { AxiosResponse } from 'axios'
import queryString from 'query-string'
import React from 'react'
import { ThunkAction, ThunkDispatch } from 'redux-thunk'
import { getOrgMembership } from 'src/app/PeopleDetailFlyout/redux/peopleDetailFlyout.actions'
import {
  getInvitedPeople,
  removeOrganizationInvite,
  RemoveOrganizationInviteActions,
} from 'src/app/PeopleInvitedToOrg/redux/peopleInvitedToOrg.actions'
import { getOrgMembers } from 'src/app/PeopleMembers/redux/peopleMembers.actions'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import {
  InviteToOrganizationToastMessage,
  UpdateOrgRoleToastMessage,
} from 'src/app/Toast/Toast'
import { OrganizationRoles, OrgIntegrations } from 'src/enums'
import {
  OrganizationInviteResponse,
  OrganizationsListResponse,
} from 'src/models/organizations.models'
import { PaginationResponse } from 'src/models/pagination.models'
import {
  OrgInviteStatus,
  PeopleInvitedToOrgResponse,
} from 'src/models/people.models'
import { UserListResponse } from 'src/models/user.models'
import { closeModal, connectedPush } from 'src/navigation/Lnk'
import { homeUrl } from 'src/navigation/urls'
import { getMe } from 'src/redux/actions/auth.actions'
import { RootState } from 'src/redux/createRootReducer'
import { selectAuthedJsonApiRequestConfig } from 'src/redux/selectors/axios.selectors'
import {
  PatchOrganizationRequest,
  PutOrganizationRequest,
} from 'src/requests/patchOrganization.request'
import { PostOrganizationRequest } from 'src/requests/postOrganization.request'
import { PostPeopleInvitesRequest } from 'src/requests/postPeopleInvites.request'
import { BackendErrorPayload } from 'src/utils/error.utils'

export const GET_ORGANIZATIONS_INIT =
  'data/organizations/GET_ORGANIZATIONS_INIT'
export const GET_ORGANIZATIONS_SUCCESS =
  'data/organizations/GET_ORGANIZATIONS_SUCCESS'
export const GET_ORGANIZATIONS_FAILURE =
  'data/organizations/GET_ORGANIZATIONS_FAILURE'

export interface GetOrganizationsSuccessPayload {
  organizations: OrganizationsListResponse[]
}
export type GetOrganizationsErrorPayload = BackendErrorPayload<string>

export interface GetOrganizationsInitAction {
  type: typeof GET_ORGANIZATIONS_INIT
}
export interface GetOrganizationsSuccessAction {
  type: typeof GET_ORGANIZATIONS_SUCCESS
  payload: GetOrganizationsSuccessPayload
}
export interface GetOrganizationsFailureAction {
  type: typeof GET_ORGANIZATIONS_FAILURE
  payload: GetOrganizationsErrorPayload
}

export type GetOrganizationsActions =
  | GetOrganizationsInitAction
  | GetOrganizationsSuccessAction
  | GetOrganizationsFailureAction

const createGetOrganizationsInitAction = (): GetOrganizationsInitAction => ({
  type: GET_ORGANIZATIONS_INIT,
})
const createGetOrganizationsSuccessAction = (
  payload: GetOrganizationsSuccessPayload,
): GetOrganizationsSuccessAction => ({
  type: GET_ORGANIZATIONS_SUCCESS,
  payload,
})

const createGetOrganizationsFailureAction = (
  payload: GetOrganizationsErrorPayload,
): GetOrganizationsFailureAction => ({
  type: GET_ORGANIZATIONS_FAILURE,
  payload,
})

export const getOrganizations = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetOrganizationsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetOrganizationsActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetOrganizationsInitAction())
    return axios
      .get<GetOrganizationsSuccessPayload>(
        `/organizations/`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetOrganizationsSuccessAction(response.data))
        },
        async (err) => {
          dispatch(createGetOrganizationsFailureAction(err.response.data))
        },
      )
  }
}

export const GET_ORGANIZATION_INIT = 'data/organizations/GET_ORGANIZATION_INIT'
export const GET_ORGANIZATION_SUCCESS =
  'data/organizations/GET_ORGANIZATION_SUCCESS'
export const GET_ORGANIZATION_FAILURE =
  'data/organizations/GET_ORGANIZATION_FAILURE'

export interface GetOrganizationSuccessPayload {
  organization: OrganizationsListResponse
}
export type GetOrganizationErrorPayload = BackendErrorPayload<string>

export interface GetOrganizationInitAction {
  type: typeof GET_ORGANIZATION_INIT
}
export interface GetOrganizationSuccessAction {
  type: typeof GET_ORGANIZATION_SUCCESS
  payload: GetOrganizationSuccessPayload
}
export interface GetOrganizationFailureAction {
  type: typeof GET_ORGANIZATION_FAILURE
  payload: GetOrganizationErrorPayload
}

export type GetOrganizationActions =
  | GetOrganizationInitAction
  | GetOrganizationSuccessAction
  | GetOrganizationFailureAction

const createGetOrganizationInitAction = (): GetOrganizationInitAction => ({
  type: GET_ORGANIZATION_INIT,
})
const createGetOrganizationSuccessAction = (
  payload: GetOrganizationSuccessPayload,
): GetOrganizationSuccessAction => ({
  type: GET_ORGANIZATION_SUCCESS,
  payload,
})

const createGetOrganizationFailureAction = (
  payload: GetOrganizationErrorPayload,
): GetOrganizationFailureAction => ({
  type: GET_ORGANIZATION_FAILURE,
  payload,
})

export const getOrganization = (
  id: string,
): ThunkAction<Promise<void>, RootState, void, GetOrganizationActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetOrganizationActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetOrganizationInitAction())
    return axios
      .get<GetOrganizationSuccessPayload>(
        `/organizations/${id}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetOrganizationSuccessAction(response.data))
        },
        async (err) => {
          dispatch(createGetOrganizationFailureAction(err.response.data))
        },
      )
  }
}

export const PATCH_ORGANIZATIONS_INIT =
  'data/organizations/PATCH_ORGANIZATIONS_INIT'
export const PATCH_ORGANIZATIONS_SUCCESS =
  'data/organizations/PATCH_ORGANIZATIONS_SUCCESS'
export const PATCH_ORGANIZATIONS_FAILURE =
  'data/organizations/PATCH_ORGANIZATIONS_FAILURE'

export type PatchOrganizationsSuccessPayload = OrganizationsListResponse

export interface PatchOrganizationsInitAction {
  type: typeof PATCH_ORGANIZATIONS_INIT
}
export interface PatchOrganizationsSuccessAction {
  type: typeof PATCH_ORGANIZATIONS_SUCCESS
  payload: PatchOrganizationsSuccessPayload
}
export interface PatchOrganizationsFailureAction {
  type: typeof PATCH_ORGANIZATIONS_FAILURE
  payload: BackendErrorPayload<string>
}

export type PatchOrganizationsActions =
  | PatchOrganizationsInitAction
  | PatchOrganizationsSuccessAction
  | PatchOrganizationsFailureAction

const createPatchOrganizationsInitAction =
  (): PatchOrganizationsInitAction => ({
    type: PATCH_ORGANIZATIONS_INIT,
  })
const createPatchOrganizationsSuccessAction = (
  payload: PatchOrganizationsSuccessPayload,
): PatchOrganizationsSuccessAction => ({
  type: PATCH_ORGANIZATIONS_SUCCESS,
  payload,
})
const createPatchOrganizationsFailureAction = (
  response: BackendErrorPayload<string>,
): PatchOrganizationsFailureAction => ({
  type: PATCH_ORGANIZATIONS_FAILURE,
  payload: response,
})

export const patchOrganizations = (
  organizationsId: string,
  body: Partial<PatchOrganizationRequest>,
): ThunkAction<
  Promise<PatchOrganizationsFailureAction | PatchOrganizationsSuccessAction>,
  RootState,
  void,
  PatchOrganizationsActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PatchOrganizationsActions>,
    getState: () => RootState,
  ): Promise<
    PatchOrganizationsFailureAction | PatchOrganizationsSuccessAction
  > => {
    dispatch(createPatchOrganizationsInitAction())
    return axios
      .patch<PatchOrganizationsSuccessPayload>(
        `/organizations/${organizationsId}`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          return dispatch(createPatchOrganizationsSuccessAction(response.data))
        },
        (err) => {
          return dispatch(
            createPatchOrganizationsFailureAction(err.response.data),
          )
        },
      )
  }
}

export const POST_ORGANIZATION_INIT =
  'data/organizations/POST_ORGANIZATION_INIT'
export const ABANDON_CREATE_ORGANIZATION =
  'data/organizations/ABANDON_CREATE_ORGANIZATION'
export const POST_ORGANIZATION_SUCCESS =
  'data/organizations/POST_ORGANIZATION_SUCCESS'
export const POST_ORGANIZATION_FAILURE =
  'data/organizations/POST_ORGANIZATION_FAILURE'

export interface PostOrganizationSuccessPayload {
  organization: OrganizationsListResponse
}

export type PostOrganizationErrorPayload = BackendErrorPayload<string>

export interface PostOrganizationInitAction {
  type: typeof POST_ORGANIZATION_INIT
}

export interface AbandonPostOrganizationAction {
  type: typeof ABANDON_CREATE_ORGANIZATION
}
export interface PostOrganizationSuccessAction {
  type: typeof POST_ORGANIZATION_SUCCESS
  payload: PostOrganizationSuccessPayload
}
export interface PostOrganizationFailureAction {
  type: typeof POST_ORGANIZATION_FAILURE
  payload: PostOrganizationErrorPayload
}

export type PostOrganizationActions =
  | PostOrganizationInitAction
  | AbandonPostOrganizationAction
  | PostOrganizationSuccessAction
  | PostOrganizationFailureAction

const createPostOrganizationInitAction = (): PostOrganizationInitAction => ({
  type: POST_ORGANIZATION_INIT,
})
export const createAbandonPostOrganizationAction =
  (): AbandonPostOrganizationAction => ({
    type: ABANDON_CREATE_ORGANIZATION,
  })
const createPostOrganizationSuccessAction = (
  payload: PostOrganizationSuccessPayload,
): PostOrganizationSuccessAction => ({
  type: POST_ORGANIZATION_SUCCESS,
  payload,
})
const createPostOrganizationFailureAction = (
  payload: PostOrganizationErrorPayload,
): PostOrganizationFailureAction => ({
  type: POST_ORGANIZATION_FAILURE,
  payload,
})

export const postCreateOrganizationAndOpenToastAndClosePopup = (
  body: Partial<PostOrganizationRequest>,
  invitedMembers?: string[],
  customCloseModalOnSuccess?: () => void,
): ThunkAction<Promise<void>, RootState, void, PostOrganizationActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PostOrganizationActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPostOrganizationInitAction())
    return axios
      .post<PostOrganizationSuccessPayload>(
        `/organizations`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPostOrganizationSuccessAction(response.data))
          dispatch(getMe()) // Updates user's organization info
          if (invitedMembers !== undefined && invitedMembers.length > 0) {
            dispatch(
              postInviteToOrganizationAndOpenToastAndCloseFlyout(
                response.data.organization.id,
                { emails: invitedMembers },
                true,
              ),
            )
          }
          if (customCloseModalOnSuccess !== undefined) {
            customCloseModalOnSuccess()
          } else {
            dispatch(closeModal())
          }
        },
        (err) => {
          dispatch(createPostOrganizationFailureAction(err.response.data))
        },
      )
  }
}

export const PUT_ORGANIZATION_INIT = 'data/organizations/PUT_ORGANIZATION_INIT'
export const EDIT_ORGANIZATION_INIT =
  'data/organizations/EDIT_ORGANIZATION_INIT'
export const PUT_ORGANIZATION_SUCCESS =
  'data/organizations/PUT_ORGANIZATION_SUCCESS'
export const PUT_ORGANIZATION_FAILURE =
  'data/organizations/PUT_ORGANIZATION_FAILURE'
export const ABANDON_EDIT_ORGANIZATION =
  'data/organizations/ABANDON_EDIT_ORGANIZATION'

export interface PutOrganizationSuccessPayload {
  organization: OrganizationsListResponse
}

export type PutOrganizationErrorPayload = BackendErrorPayload<string>

export interface PutOrganizationInitAction {
  type: typeof PUT_ORGANIZATION_INIT
}

export interface EditOrganizationInitAction {
  type: typeof EDIT_ORGANIZATION_INIT
}
export interface PutOrganizationSuccessAction {
  type: typeof PUT_ORGANIZATION_SUCCESS
  payload: PutOrganizationSuccessPayload
}
export interface PutOrganizationFailureAction {
  type: typeof PUT_ORGANIZATION_FAILURE
  payload: PutOrganizationErrorPayload
}

export interface AbandonEditOrganizationAction {
  type: typeof ABANDON_EDIT_ORGANIZATION
}

export type PutOrganizationActions =
  | PutOrganizationInitAction
  | EditOrganizationInitAction
  | PutOrganizationSuccessAction
  | PutOrganizationFailureAction
  | AbandonEditOrganizationAction

const createPutOrganizationInitAction = (): PutOrganizationInitAction => ({
  type: PUT_ORGANIZATION_INIT,
})

export const createEditOrganizationInitAction =
  (): EditOrganizationInitAction => ({
    type: EDIT_ORGANIZATION_INIT,
  })
export const createPutOrganizationSuccessAction = (
  payload: PutOrganizationSuccessPayload,
): PutOrganizationSuccessAction => ({
  type: PUT_ORGANIZATION_SUCCESS,
  payload,
})
const createPutOrganizationFailureAction = (
  payload: PutOrganizationErrorPayload,
): PutOrganizationFailureAction => ({
  type: PUT_ORGANIZATION_FAILURE,
  payload,
})
export const createAbandonEditOrganizationAction =
  (): AbandonEditOrganizationAction => ({
    type: ABANDON_EDIT_ORGANIZATION,
  })

export const putOrganization = (
  id: string,
  body: Partial<PutOrganizationRequest>,
): ThunkAction<Promise<void>, RootState, void, PutOrganizationActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutOrganizationActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutOrganizationInitAction())
    return axios
      .put<PutOrganizationSuccessPayload>(
        `/organizations/${id}`,
        body,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutOrganizationSuccessAction(response.data))
        },
        (err) => {
          dispatch(createPutOrganizationFailureAction(err.response.data))
        },
      )
  }
}

export const DELETE_ORGANIZATION_INIT =
  'data/organizations/DELETE_ORGANIZATION_INIT'
export const DELETE_ORGANIZATION_SUCCESS =
  'data/organizations/DELETE_ORGANIZATION_SUCCESS'
export const DELETE_ORGANIZATION_FAILURE =
  'data/organizations/DELETE_ORGANIZATION_FAILURE'

export interface DeleteOrganizationSuccessPayload {
  organizations: OrganizationsListResponse[]
}

export type DeleteOrganizationErrorPayload = BackendErrorPayload<string>

export interface DeleteOrganizationInitAction {
  type: typeof DELETE_ORGANIZATION_INIT
}
export interface DeleteOrganizationSuccessAction {
  type: typeof DELETE_ORGANIZATION_SUCCESS
  payload: DeleteOrganizationSuccessPayload
}
export interface DeleteOrganizationFailureAction {
  type: typeof DELETE_ORGANIZATION_FAILURE
  payload: DeleteOrganizationErrorPayload
}

export type DeleteOrganizationActions =
  | DeleteOrganizationInitAction
  | DeleteOrganizationSuccessAction
  | DeleteOrganizationFailureAction

const createDeleteOrganizationInitAction =
  (): DeleteOrganizationInitAction => ({
    type: DELETE_ORGANIZATION_INIT,
  })
const createDeleteOrganizationSuccessAction = (
  payload: DeleteOrganizationSuccessPayload,
): DeleteOrganizationSuccessAction => ({
  type: DELETE_ORGANIZATION_SUCCESS,
  payload,
})
const createDeleteOrganizationFailureAction = (
  payload: DeleteOrganizationErrorPayload,
): DeleteOrganizationFailureAction => ({
  type: DELETE_ORGANIZATION_FAILURE,
  payload,
})

export const deleteOrganization = (
  id: string,
): ThunkAction<Promise<void>, RootState, void, DeleteOrganizationActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, DeleteOrganizationActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createDeleteOrganizationInitAction())
    return axios
      .delete<DeleteOrganizationSuccessPayload>(
        `/organizations/${id}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createDeleteOrganizationSuccessAction(response.data))
          dispatch(closeModal())
        },
        (err) => {
          dispatch(createDeleteOrganizationFailureAction(err.response.data))
        },
      )
  }
}

export const INVITE_ORGANIZATION_INIT =
  'data/organizations/INVITE_ORGANIZATION_INIT'
export const INVITE_ORGANIZATION_SUCCESS =
  'data/organizations/INVITE_ORGANIZATION_SUCCESS'
export const INVITE_ORGANIZATION_FAILURE =
  'data/organizations/INVITE_ORGANIZATION_FAILURE'

export type InviteOrganizationSuccessPayload = PeopleInvitedToOrgResponse

export type InviteOrganizationErrorPayload = BackendErrorPayload<string>

export interface InviteOrganizationInitAction {
  type: typeof INVITE_ORGANIZATION_INIT
}
export interface InviteOrganizationSuccessAction {
  type: typeof INVITE_ORGANIZATION_SUCCESS
  payload: InviteOrganizationSuccessPayload
}
export interface InviteOrganizationFailureAction {
  type: typeof INVITE_ORGANIZATION_FAILURE
  payload: InviteOrganizationErrorPayload
}

export type InviteOrganizationActions =
  | InviteOrganizationInitAction
  | InviteOrganizationSuccessAction
  | InviteOrganizationFailureAction

const createInviteOrganizationInitAction =
  (): InviteOrganizationInitAction => ({
    type: INVITE_ORGANIZATION_INIT,
  })
const createInviteOrganizationSuccessAction = (
  payload: InviteOrganizationSuccessPayload,
): InviteOrganizationSuccessAction => ({
  type: INVITE_ORGANIZATION_SUCCESS,
  payload,
})
const createInviteOrganizationFailureAction = (
  payload: InviteOrganizationErrorPayload,
): InviteOrganizationFailureAction => ({
  type: INVITE_ORGANIZATION_FAILURE,
  payload,
})

const successfulOrgInviteActions = (
  dispatch: ThunkDispatch<RootState, void, InviteOrganizationActions>,
  organizationId: string,
  response: AxiosResponse<PeopleInvitedToOrgResponse, unknown>,
  keepFlyoutOpen?: boolean,
  repeat?: boolean,
  guest?: boolean,
): void => {
  dispatch(
    getInvitedPeople(organizationId, {
      page: 0,
      perPage: 10,
    }),
  )
  if (!keepFlyoutOpen) {
    dispatch(closeModal())
  }
  dispatch(
    createSetIsToastOpen(true, {
      toastMessage: (
        <InviteToOrganizationToastMessage
          people={response.data?.organizationInvites}
          repeat={repeat}
          guest={guest}
        />
      ),
    }),
  )
}

export const postInviteToOrganizationAndOpenToastAndCloseFlyout = (
  organizationId: string,
  body: Partial<PostPeopleInvitesRequest>,
  silent?: boolean,
  keepFlyoutOpen?: boolean,
  repeat?: boolean,
): ThunkAction<
  Promise<InviteOrganizationSuccessAction | InviteOrganizationFailureAction>,
  RootState,
  void,
  InviteOrganizationActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, InviteOrganizationActions>,
    getState: () => RootState,
  ): Promise<
    InviteOrganizationSuccessAction | InviteOrganizationFailureAction
  > => {
    dispatch(createInviteOrganizationInitAction())
    return axios
      .post<InviteOrganizationSuccessPayload>(
        `/organizations/${organizationId}/invite`,
        {
          emails: body.emails,
          role: body.role,
          bookkeeperAccountVisibilityType: body.bookkeeperAccountVisibilityType,
          bookkeeperAccounts: body.bookkeeperAccounts,
          cardManagerAccounts: body.cardManagerAccounts,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          const createPostInviteToOrganizationActionDispatched = dispatch(
            createInviteOrganizationSuccessAction(response.data),
          )
          const isInvitingGuests = body.role === OrganizationRoles.guest
          if (!silent) {
            successfulOrgInviteActions(
              dispatch,
              organizationId,
              response,
              keepFlyoutOpen,
              repeat,
              isInvitingGuests,
            )
          }
          return createPostInviteToOrganizationActionDispatched
        },
        (err) => {
          const createPostInviteToOrganizationActionDispatched = dispatch(
            createInviteOrganizationFailureAction(err.response.data),
          )
          return createPostInviteToOrganizationActionDispatched
        },
      )
  }
}

export const GET_USER_ORGANIZATION_INVITES_INIT =
  'data/organizations/GET_USER_ORGANIZATION_INVITES_INIT'
export const GET_USER_ORGANIZATION_INVITES_SUCCESS =
  'data/organizations/GET_USER_ORGANIZATION_INVITES_SUCCESS'
export const GET_USER_ORGANIZATION_INVITES_FAILURE =
  'data/organizations/GET_USER_ORGANIZATION_INVITES_FAILURE'

export interface GetUserOrganizationInvitesSuccessPayload {
  organizationInvites: OrganizationInviteResponse[]
}

export type GetUserOrganizationInvitesErrorPayload = BackendErrorPayload<string>

export interface GetUserOrganizationInvitesQueryParams {
  sortDirection?: string
  sortField?: string
}

export interface GetUserOrganizationInvitesInitAction {
  type: typeof GET_USER_ORGANIZATION_INVITES_INIT
}
export interface GetUserOrganizationInvitesSuccessAction {
  type: typeof GET_USER_ORGANIZATION_INVITES_SUCCESS
  payload: GetUserOrganizationInvitesSuccessPayload
}
export interface GetUserOrganizationInvitesFailureAction {
  type: typeof GET_USER_ORGANIZATION_INVITES_FAILURE
  payload: GetUserOrganizationInvitesErrorPayload
}

export type GetUserOrganizationInvitesActions =
  | GetUserOrganizationInvitesInitAction
  | GetUserOrganizationInvitesSuccessAction
  | GetUserOrganizationInvitesFailureAction

const createGetUserOrganizationInvitesInitAction =
  (): GetUserOrganizationInvitesInitAction => ({
    type: GET_USER_ORGANIZATION_INVITES_INIT,
  })
const createGetUserOrganizationInvitesSuccessAction = (
  payload: GetUserOrganizationInvitesSuccessPayload,
): GetUserOrganizationInvitesSuccessAction => ({
  type: GET_USER_ORGANIZATION_INVITES_SUCCESS,
  payload,
})
const createGetUserOrganizationInvitesFailureAction = (
  payload: GetUserOrganizationInvitesErrorPayload,
): GetUserOrganizationInvitesFailureAction => ({
  type: GET_USER_ORGANIZATION_INVITES_FAILURE,
  payload,
})

export const getUserOrganizationInvites = (
  queryParameters?: GetUserOrganizationInvitesQueryParams,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetUserOrganizationInvitesActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetUserOrganizationInvitesActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetUserOrganizationInvitesInitAction())
    return axios
      .get<GetUserOrganizationInvitesSuccessPayload>(
        `/organizationinvites?${queryParameters ? queryString.stringify(queryParameters) : ''}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetUserOrganizationInvitesSuccessAction(response.data))
        },
        (err) => {
          dispatch(
            createGetUserOrganizationInvitesFailureAction(err.response.data),
          )
        },
      )
  }
}

export const PUT_ORGANIZATION_INVITE_INIT =
  'data/organizations/PUT_ORGANIZATION_INVITE_INIT'
export const PUT_ORGANIZATION_INVITE_SUCCESS =
  'data/organizations/PUT_ORGANIZATION_INVITE_SUCCESS'
export const PUT_ORGANIZATION_INVITE_FAILURE =
  'data/organizations/PUT_ORGANIZATION_INVITE_FAILURE'
export const ABANDON_ACCEPT_ORGANIZATION_INVITE =
  'data/organizations/ABANDON_ACCEPT_ORGANIZATION_INVITE'

export interface PutOrganizationInviteSuccessPayload {
  organizationInvite: OrganizationInviteResponse
}

export type PutOrganizationInviteErrorPayload = BackendErrorPayload<string>

export interface PutOrganizationInviteInitAction {
  type: typeof PUT_ORGANIZATION_INVITE_INIT
}
export interface PutOrganizationInviteSuccessAction {
  type: typeof PUT_ORGANIZATION_INVITE_SUCCESS
  payload: PutOrganizationInviteSuccessPayload
}
export interface PutOrganizationInviteFailureAction {
  type: typeof PUT_ORGANIZATION_INVITE_FAILURE
  payload: PutOrganizationInviteErrorPayload
}

export interface AbandonAcceptOrganizationInviteAction {
  type: typeof ABANDON_ACCEPT_ORGANIZATION_INVITE
}

export type PutOrganizationInviteActions =
  | PutOrganizationInviteInitAction
  | PutOrganizationInviteSuccessAction
  | PutOrganizationInviteFailureAction
  | AbandonAcceptOrganizationInviteAction

const createPutOrganizationInviteInitAction =
  (): PutOrganizationInviteInitAction => ({
    type: PUT_ORGANIZATION_INVITE_INIT,
  })
const createPutOrganizationInviteSuccessAction = (
  payload: PutOrganizationInviteSuccessPayload,
): PutOrganizationInviteSuccessAction => ({
  type: PUT_ORGANIZATION_INVITE_SUCCESS,
  payload,
})
const createPutOrganizationInviteFailureAction = (
  payload: PutOrganizationInviteErrorPayload,
): PutOrganizationInviteFailureAction => ({
  type: PUT_ORGANIZATION_INVITE_FAILURE,
  payload,
})
export const createAbandonOrganizationInviteAction =
  (): AbandonAcceptOrganizationInviteAction => ({
    type: ABANDON_ACCEPT_ORGANIZATION_INVITE,
  })

// MIGRATED (auth-redux slice 4) → src/api/mutation/organizationInvite.tsx
// `usePutOrganizationInvite`. The org-invite accept/reject/cancel decision flow.
// The three DIRECT component consumers (ManageOrgInvite, CancelOrgInvitePopup,
// OrgInviteDecision) were re-pointed to the mutation, which reimplements this
// body byte-for-byte (same authed PUT, same PUT_ORGANIZATION_INVITE_* actions,
// same success cascade + toast + connectedPush). TWO internal callers remain:
// the `acceptOrgInvite` auto-accept inside the login thunks in auth.actions.tsx
// (postSigninThenPush + getCognitoSigninThenPush family) — they fuse with
// sign-in and migrate with the login / SSO / Cognito family in a later slice.
// Retained until then (and the slice-6 deletion). The action-type constants +
// payloads + union above stay (the OrganizationInvites reducer reads them).
export const putOrganizationInvite = (
  status: OrgInviteStatus,
  organizationInvite: OrganizationInviteResponse,
  toast?: React.ReactElement,
  setHasAcceptedInvite?: (val: boolean) => void,
  onSignup?: boolean,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  PutOrganizationInviteActions | RemoveOrganizationInviteActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      PutOrganizationInviteActions | RemoveOrganizationInviteActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutOrganizationInviteInitAction())
    if (organizationInvite) {
      return axios
        .put<PutOrganizationInviteSuccessPayload>(
          `/organizationinvites/${organizationInvite?.id}`,
          { status: status },
          selectAuthedJsonApiRequestConfig(getState()),
        )
        .then(
          async (response) => {
            const acceptedInvite = status === OrgInviteStatus.Accepted
            dispatch(createPutOrganizationInviteSuccessAction(response.data))
            if (acceptedInvite) {
              dispatch(getMe())
              dispatch(getOrganizations())
            } else {
              // canceled or rejected
              dispatch(
                getUserOrganizationInvites({
                  sortDirection: 'ASC',
                  sortField: 'createdAt',
                }),
              )
              dispatch(
                removeOrganizationInvite(response.data.organizationInvite.id),
              )
              dispatch(
                getInvitedPeople(organizationInvite.organization.id, {
                  page: 0,
                  count: 10,
                }),
              )
              if (toast) {
                dispatch(
                  createSetIsToastOpen(true, {
                    toastMessage: toast,
                  }),
                )
              }
            }
            if (setHasAcceptedInvite) {
              setHasAcceptedInvite(acceptedInvite)
            }
            if (onSignup) {
              dispatch(connectedPush(homeUrl()))
            }
            dispatch(getOrganizationInvites())
          },
          (err) => {
            dispatch(
              createPutOrganizationInviteFailureAction(err.response.data),
            )
          },
        )
    }
  }
}

export const GET_ORGANIZATION_INVITE_INIT =
  'data/organizations/GET_ORGANIZATION_INVITE_INIT'
export const GET_ORGANIZATION_INVITE_SUCCESS =
  'data/organizations/GET_ORGANIZATION_INVITE_SUCCESS'
export const GET_ORGANIZATION_INVITE_FAILURE =
  'data/organizations/GET_ORGANIZATION_INVITE_FAILURE'

export interface GetOrganizationInviteSuccessPayload {
  organizationInvite: OrganizationInviteResponse
}
export type GetOrganizationInviteErrorPayload = BackendErrorPayload<string>

export interface GetOrganizationInviteInitAction {
  type: typeof GET_ORGANIZATION_INVITE_INIT
}
export interface GetOrganizationInviteSuccessAction {
  type: typeof GET_ORGANIZATION_INVITE_SUCCESS
  payload: GetOrganizationInviteSuccessPayload
}
export interface GetOrganizationInviteFailureAction {
  type: typeof GET_ORGANIZATION_INVITE_FAILURE
  payload: GetOrganizationInviteErrorPayload
}

export type GetOrganizationInviteActions =
  | GetOrganizationInviteInitAction
  | GetOrganizationInviteSuccessAction
  | GetOrganizationInviteFailureAction

const createGetOrganizationInviteInitAction =
  (): GetOrganizationInviteInitAction => ({
    type: GET_ORGANIZATION_INVITE_INIT,
  })
const createGetOrganizationInviteSuccessAction = (
  payload: GetOrganizationInviteSuccessPayload,
): GetOrganizationInviteSuccessAction => ({
  type: GET_ORGANIZATION_INVITE_SUCCESS,
  payload,
})

const createGetOrganizationInviteFailureAction = (
  payload: GetOrganizationInviteErrorPayload,
): GetOrganizationInviteFailureAction => ({
  type: GET_ORGANIZATION_INVITE_FAILURE,
  payload,
})

export const getOrganizationInvite = (
  inviteId: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetOrganizationInviteActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetOrganizationInviteActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetOrganizationInviteInitAction())
    return axios
      .get<GetOrganizationInviteSuccessPayload>(
        `/organizationinvites/${inviteId}`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetOrganizationInviteSuccessAction(response.data))
        },
        async (err) => {
          dispatch(createGetOrganizationInviteFailureAction(err.response.data))
        },
      )
  }
}

export const GET_ORGANIZATION_INVITES_INIT =
  'data/organizations/GET_ORGANIZATION_INVITES_INIT'
export const GET_ORGANIZATION_INVITES_SUCCESS =
  'data/organizations/GET_ORGANIZATION_INVITES_SUCCESS'
export const GET_ORGANIZATION_INVITES_FAILURE =
  'data/organizations/GET_ORGANIZATION_INVITES_FAILURE'

export interface GetOrganizationInvitesSuccessPayload {
  pagination: PaginationResponse
  organizationInvites: OrganizationInviteResponse[]
}
export type GetOrganizationInvitesErrorPayload = BackendErrorPayload<string>

export interface GetOrganizationInvitesInitAction {
  type: typeof GET_ORGANIZATION_INVITES_INIT
}
export interface GetOrganizationInvitesSuccessAction {
  type: typeof GET_ORGANIZATION_INVITES_SUCCESS
  payload: GetOrganizationInvitesSuccessPayload
}
export interface GetOrganizationInvitesFailureAction {
  type: typeof GET_ORGANIZATION_INVITES_FAILURE
  payload: GetOrganizationInvitesErrorPayload
}

export type GetOrganizationInvitesActions =
  | GetOrganizationInvitesInitAction
  | GetOrganizationInvitesSuccessAction
  | GetOrganizationInvitesFailureAction

const createGetOrganizationInvitesInitAction =
  (): GetOrganizationInvitesInitAction => ({
    type: GET_ORGANIZATION_INVITES_INIT,
  })
const createGetOrganizationInvitesSuccessAction = (
  payload: GetOrganizationInvitesSuccessPayload,
): GetOrganizationInvitesSuccessAction => ({
  type: GET_ORGANIZATION_INVITES_SUCCESS,
  payload,
})

const createGetOrganizationInvitesFailureAction = (
  payload: GetOrganizationInviteErrorPayload,
): GetOrganizationInvitesFailureAction => ({
  type: GET_ORGANIZATION_INVITES_FAILURE,
  payload,
})

export const getOrganizationInvites = (): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetOrganizationInvitesActions
> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, GetOrganizationInvitesActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetOrganizationInvitesInitAction())
    return axios
      .get<GetOrganizationInvitesSuccessPayload>(
        `/organizationinvites`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(createGetOrganizationInvitesSuccessAction(response.data))
        },
        async (err) => {
          dispatch(createGetOrganizationInvitesFailureAction(err.response.data))
        },
      )
  }
}

export const PUT_ORGANIZATION_ROLE_INIT =
  'data/organizations/PUT_ORGANIZATION_ROLE_INIT'
export const PUT_ORGANIZATION_ROLE_SUCCESS =
  'data/organizations/PUT_ORGANIZATION_ROLE_SUCCESS'
export const PUT_ORGANIZATION_ROLE_FAILURE =
  'data/organizations/PUT_ORGANIZATION_ROLE_FAILURE'
export const RESET_PUT_ORGANIZATION_ROLE_ERROR =
  'data/organizations/RESET_PUT_ORGANIZATION_ROLE_ERROR'

export type PutOrganizationRoleSuccessPayload = UserListResponse

export type PutOrganizationRoleErrorPayload = BackendErrorPayload<string>

export interface PutOrganizationRoleInitAction {
  type: typeof PUT_ORGANIZATION_ROLE_INIT
}
export interface PutOrganizationRoleSuccessAction {
  type: typeof PUT_ORGANIZATION_ROLE_SUCCESS
  payload: PutOrganizationRoleSuccessPayload
}
export interface PutOrganizationRoleFailureAction {
  type: typeof PUT_ORGANIZATION_ROLE_FAILURE
  payload: PutOrganizationRoleErrorPayload
}

export type PutOrganizationRoleActions =
  | PutOrganizationRoleInitAction
  | PutOrganizationRoleSuccessAction
  | PutOrganizationRoleFailureAction
  | ResetPutOrganizationRoleAction

const createPutOrganizationRoleInitAction =
  (): PutOrganizationRoleInitAction => ({
    type: PUT_ORGANIZATION_ROLE_INIT,
  })
const createPutOrganizationRoleSuccessAction = (
  payload: PutOrganizationRoleSuccessPayload,
): PutOrganizationRoleSuccessAction => ({
  type: PUT_ORGANIZATION_ROLE_SUCCESS,
  payload,
})
const createPutOrganizationRoleFailureAction = (
  payload: PutOrganizationRoleErrorPayload,
): PutOrganizationRoleFailureAction => ({
  type: PUT_ORGANIZATION_ROLE_FAILURE,
  payload,
})

export interface ResetPutOrganizationRoleAction {
  type: typeof RESET_PUT_ORGANIZATION_ROLE_ERROR
}

export const putOrganizationRole = (
  id: string,
  userId: string,
  firstName: string,
  lastName: string,
  roleString: React.ReactNode,
  isCurrentUser: boolean,
  body: Partial<PostPeopleInvitesRequest>,
): ThunkAction<Promise<void>, RootState, void, PutOrganizationRoleActions> => {
  return async (
    dispatch: ThunkDispatch<RootState, void, PutOrganizationRoleActions>,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createPutOrganizationRoleInitAction())
    return axios
      .put<PutOrganizationRoleSuccessPayload>(
        `/organizations/${id}/member`,
        {
          ...body,
          userId: userId,
        },
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        (response) => {
          dispatch(createPutOrganizationRoleSuccessAction(response.data))
          dispatch(getMe()) // helps to update current user's permission
          dispatch(getOrgMembership(userId, id)) // helps to update current user's permission
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: (
                <UpdateOrgRoleToastMessage
                  isCurrentUser={isCurrentUser}
                  userName={`${firstName} ${lastName}`}
                  userRole={roleString}
                />
              ),
              isDarkBackground: true,
            }),
          )
          dispatch(getOrganization(id)) // helps to check the current org owner (check permission for update dropdown)
          dispatch(getOrgMembers(id, { renderMetrics: true })) // helps to update role tag on the member list
        },
        (err) => {
          dispatch(createPutOrganizationRoleFailureAction(err.response.data))
        },
      )
  }
}

export const GET_ORGANIZATION_INTEGRATIONS_INIT =
  'data/organizations/GET_ORGANIZATION_INTEGRATIONS_INIT'
export const GET_ORGANIZATION_INTEGRATIONS_SUCCESS =
  'data/organizations/GET_ORGANIZATION_INTEGRATIONS_SUCCESS'
export const GET_ORGANIZATION_INTEGRATIONS_FAILURE =
  'data/organizations/GET_ORGANIZATION_INTEGRATIONS_FAILURE'

export interface GetOrganizationIntegrationsSuccessPayload {
  integrations: OrgIntegrations[]
}
export type GetOrganizationIntegrationsErrorPayload =
  BackendErrorPayload<string>

export interface GetOrganizationIntegrationsInitAction {
  type: typeof GET_ORGANIZATION_INTEGRATIONS_INIT
}
export interface GetOrganizationIntegrationsSuccessAction {
  type: typeof GET_ORGANIZATION_INTEGRATIONS_SUCCESS
  payload: GetOrganizationIntegrationsSuccessPayload
  orgId: string
}
export interface GetOrganizationIntegrationsFailureAction {
  type: typeof GET_ORGANIZATION_INTEGRATIONS_FAILURE
  payload: GetOrganizationIntegrationsErrorPayload
}

export type GetOrganizationIntegrationsActions =
  | GetOrganizationIntegrationsInitAction
  | GetOrganizationIntegrationsSuccessAction
  | GetOrganizationIntegrationsFailureAction

const createGetOrganizationIntegrationsInitAction =
  (): GetOrganizationIntegrationsInitAction => ({
    type: GET_ORGANIZATION_INTEGRATIONS_INIT,
  })
const createGetOrganizationIntegrationsSuccessAction = (
  payload: GetOrganizationIntegrationsSuccessPayload,
  orgId: string,
): GetOrganizationIntegrationsSuccessAction => ({
  type: GET_ORGANIZATION_INTEGRATIONS_SUCCESS,
  payload,
  orgId,
})

const createGetOrganizationIntegrationsFailureAction = (
  payload: GetOrganizationIntegrationsErrorPayload,
): GetOrganizationIntegrationsFailureAction => ({
  type: GET_ORGANIZATION_INTEGRATIONS_FAILURE,
  payload,
})

export const getOrganizationIntegrations = (
  id: string,
): ThunkAction<
  Promise<void>,
  RootState,
  void,
  GetOrganizationIntegrationsActions
> => {
  return async (
    dispatch: ThunkDispatch<
      RootState,
      void,
      GetOrganizationIntegrationsActions
    >,
    getState: () => RootState,
  ): Promise<void> => {
    dispatch(createGetOrganizationIntegrationsInitAction())
    return axios
      .get<GetOrganizationIntegrationsSuccessPayload>(
        `/organizations/${id}/integrations`,
        selectAuthedJsonApiRequestConfig(getState()),
      )
      .then(
        async (response) => {
          dispatch(
            createGetOrganizationIntegrationsSuccessAction(response.data, id),
          )
        },
        async (err) => {
          dispatch(
            createGetOrganizationIntegrationsFailureAction(err.response.data),
          )
        },
      )
  }
}
